import { useState, useEffect } from 'react'

interface CrisisResource {
  id: string
  name: string
  phone?: string
  text_number?: string
  chat_url?: string
  website?: string
}

interface CrisisOverlayProps {
  isOpen: boolean
  onClose: () => void
  detectedKeyword?: string
}

// Crisis keywords to detect (case-insensitive)
const CRISIS_KEYWORDS = [
  'suicide',
  'suicidal',
  'kill myself',
  'end my life',
  'want to die',
  'better off dead',
  'self-harm',
  'self harm',
  'hurt myself',
  'cutting',
  'overdose',
  'no reason to live',
  'can\'t go on',
  'ending it all',
  'give up on life',
]

// Check text for crisis keywords
export function detectCrisisKeywords(text: string): string | null {
  const lowerText = text.toLowerCase()
  for (const keyword of CRISIS_KEYWORDS) {
    if (lowerText.includes(keyword)) {
      return keyword
    }
  }
  return null
}

export function CrisisOverlay({ isOpen, onClose, detectedKeyword }: CrisisOverlayProps) {
  const [resources, setResources] = useState<CrisisResource[]>([])

  useEffect(() => {
    // Fetch crisis resources
    fetch('/api/crisis-resources')
      .then(res => res.json())
      .then(data => setResources(data.resources || []))
      .catch(console.error)
  }, [])

  if (!isOpen) return null

  // Find the 988 hotline for priority display
  const hotline988 = resources.find(r => r.phone === '988')
  const otherResources = resources.filter(r => r.phone !== '988')

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
            💙
          </div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            color: 'var(--color-text)'
          }}>
            We're Here For You
          </h2>
          <p style={{
            color: 'var(--color-text-muted)',
            fontSize: '0.9375rem',
            lineHeight: 1.5,
          }}>
            It sounds like you might be going through something really difficult right now.
            {detectedKeyword && (
              <span style={{ display: 'block', marginTop: '0.5rem', fontStyle: 'italic' }}>
                Your feelings are valid, and help is available.
              </span>
            )}
          </p>
        </div>

        {/* Primary crisis resource - 988 */}
        {hotline988 && (
          <a
            href={`tel:${hotline988.phone}`}
            style={{
              display: 'block',
              backgroundColor: 'var(--color-error)',
              color: 'white',
              padding: '1.25rem',
              borderRadius: '0.75rem',
              textDecoration: 'none',
              marginBottom: '1rem',
              transition: 'transform 0.2s',
            }}
          >
            <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.25rem' }}>
              Call 988
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
              {hotline988.name}
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
              Free • Confidential • 24/7
            </div>
          </a>
        )}

        {/* Text option */}
        {resources.find(r => r.text_number) && (
          <a
            href={`sms:${resources.find(r => r.text_number)?.text_number}`}
            style={{
              display: 'block',
              backgroundColor: 'var(--color-bg-light)',
              color: 'var(--color-text)',
              padding: '1rem',
              borderRadius: '0.75rem',
              textDecoration: 'none',
              marginBottom: '0.75rem',
              border: '1px solid var(--color-border)',
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
              Text HOME to 741741
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              Crisis Text Line
            </div>
          </a>
        )}

        {/* Chat option */}
        {hotline988?.chat_url && (
          <a
            href={hotline988.chat_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              backgroundColor: 'var(--color-bg-light)',
              color: 'var(--color-text)',
              padding: '1rem',
              borderRadius: '0.75rem',
              textDecoration: 'none',
              marginBottom: '1.5rem',
              border: '1px solid var(--color-border)',
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
              Chat Online
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              988lifeline.org/chat
            </div>
          </a>
        )}

        {/* International */}
        <a
          href="https://findahelpline.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            color: 'var(--color-primary)',
            fontSize: '0.875rem',
            textDecoration: 'underline',
            marginBottom: '2rem',
          }}
        >
          Find help in your country →
        </a>

        {/* Dismiss button */}
        <div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-muted)',
              border: '1px solid var(--color-border)',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            I'm okay, continue with my session
          </button>
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-muted)',
            marginTop: '0.75rem',
            opacity: 0.7,
          }}>
            Your session will be saved regardless
          </p>
        </div>
      </div>
    </div>
  )
}
